import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { AppSidebar } from "~/components/app-sidebar";
import { DropZoneOverlay } from "~/components/chat/drop-zone-overlay";
import { CommandPalette } from "~/components/command-palette";
import { GlobalHeader } from "~/components/global-header";
import { PageSkeleton } from "~/components/loading-skeleton";
import { AutoSyncController } from "~/components/sync/auto-sync-controller";
import { CloudPullController } from "~/components/sync/cloud-pull-controller";
import { SidebarInset, SidebarProvider } from "~/components/ui/sidebar";
import {
  ImageDropZoneProvider,
  useImageDropZoneContext,
} from "~/hooks/use-image-drop-zone";
import { useKeyboardShortcut } from "~/hooks/use-keyboard-shortcut";
import { useTemplateShortcuts } from "~/hooks/use-template-shortcuts";
import { CommandPaletteProvider, useCommandPalette } from "~/lib/command-palette/context";
import { $getUserPreferences } from "~/lib/queries";

export const Route = createFileRoute("/(authenticated)/dashboard")({
  component: DashboardLayout,
  pendingComponent: PageSkeleton,
  wrapInSuspense: true,
  beforeLoad: async () => {
    const prefs = await $getUserPreferences();
    if (!prefs?.hasCompletedOnboarding) {
      throw redirect({ to: "/onboarding" });
    }
  },
});

function DashboardLayout() {
  return (
    <CommandPaletteProvider>
      <ImageDropZoneProvider>
        <DashboardContent />
      </ImageDropZoneProvider>
    </CommandPaletteProvider>
  );
}

function DashboardContent() {
  const { user } = Route.useRouteContext();
  const { toggle } = useCommandPalette();
  const { isDragging, dragProps } = useImageDropZoneContext();

  useTemplateShortcuts();

  const shortcutOptions = useMemo(() => ({ metaKey: true }), []);
  const stableToggle = useCallback(() => toggle(), [toggle]);
  useKeyboardShortcut("k", stableToggle, shortcutOptions);

  return (
    <div {...dragProps}>
      <DropZoneOverlay isVisible={isDragging} />
      <SidebarProvider
        style={
          {
            "--sidebar-width": "19rem",
          } as React.CSSProperties
        }
      >
        <AppSidebar user={user} />
        <SidebarInset>
          <GlobalHeader />
          <AutoSyncController />
          <CloudPullController />
          <div className="flex flex-1 flex-col gap-4 p-4">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
      <CommandPalette />
    </div>
  );
}
